import React, { useEffect, useState } from 'react';
import { Box, Grid, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    StyledBasketBox,
    StyledButtonTable,
    StyledDashboardHeading,
    StyledGreenButton,
    StyledImage,
    StyledMediumTypography,
    StyledModalButtons,
    StyledNormalTypography,
    StyledRowBox,
    StyledWhiteButton
} from '../../theme/StyledElements';
import { Paniers } from '../../models/Paniers';
import { RadioButtonSelectType } from '../../components/RadioButtonSelectType';
import {
    deleteSpecificBasket,
    getSpecificBasket,
    updateBasketStatus
} from '../../services/cloudFirestore';
import { useNavigate, useParams } from 'react-router-dom';
import { getImageKitUrlFrom } from '../../utils';
import { BasketIndicators } from '../../components/Basket/BasketIndicators';
import { DeleteModal } from '../../components/DeleteModal';
import { useMountedRef } from '../../hooks/useMountedRef';
import { useGlobalState } from '../../providers/GlobalStateProvider';

const StyledBasketImage = styled(StyledImage)(() => ({
    marginRight: '10px',
    marginBottom: '20px'
}));

const StyledImageGrid = styled(Grid)(() => ({
    display: 'flex',
    marginTop: '10px'
}));

const StyledBasketDescription = styled(StyledNormalTypography)(() => ({
    marginTop: '10px',
    marginBottom: '10px',
    maxWidth: '845px',
    marginRight: '100px'
}));

export function ViewBasket(): React.ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const mounted = useMountedRef();
    const { setState, state } = useGlobalState();
    const { id } = useParams<{ id: string }>();
    const [panier, setPanier] = useState<Paniers>({
        idPanier: undefined,
        idProducteur: undefined,
        titre: undefined,
        poids: undefined,
        prix: undefined,
        quantite: 0,
        photoUrl: undefined,
        detail: undefined,
        estPublie: undefined,
        satisfaction: undefined,
        paniersVendus: undefined
    });
    const [selectedValue, setSelectedValue] = useState('');
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);

    useEffect(() => {
        if (panier) {
            const isBasketFound = state.baskets.findIndex(
                (obj) => obj.idPanier === panier.idPanier
            );
            if (isBasketFound === -1) {
                const list = state.baskets ?? [];
                list.push(panier);
                setState((prev) => ({
                    ...prev,
                    baskets: list
                }));
            }
        }
    }, [panier, setState]);

    useEffect(() => {
        if (id && mounted.current) {
            getSpecificBasket(id).then((data) => {
                if (data && mounted.current) {
                    setPanier(data);
                    if (data.estPublie && mounted.current) {
                        setSelectedValue(t('published'));
                    } else {
                        setSelectedValue(t('onBreak'));
                    }
                }
            });
        }
    }, [id]);

    const updateStatus = async (): Promise<void> => {
        if (id) {
            const status = selectedValue === t('published');
            await updateBasketStatus(id, status);
            setIsEditMode(false);
            setPanier({ ...panier, estPublie: status });
        }
    };

    const getBasketImages = (): (JSX.Element | null)[] | null => {
        if (panier.photoUrl) {
            return panier.photoUrl.map((item: string | File, index: number) => {
                if (typeof item === 'string' && item.length > 0) {
                    return (
                        <Grid key={index} item>
                            <StyledBasketImage src={getImageKitUrlFrom(item)} />
                        </Grid>
                    );
                }
                return null;
            });
        }
        return null;
    };

    const deleteBasket = async (): Promise<void> => {
        if (panier.idPanier) {
            await deleteSpecificBasket(panier.idPanier);
            window.location.reload();
        }
    };

    return (
        <StyledBasketBox>
            <DeleteModal
                isModelOpen={openDeleteModal}
                setModelOpen={setOpenDeleteModal}
                title={t('deleteBasket')}
                description1={t('confirmDeleteBasket1', { basketName: panier.titre })}
                description2={t('confirmDeleteMessage2')}
                onCancelClick={(): void => {
                    setOpenDeleteModal(false);
                }}
                onDeleteClick={(): void => {
                    setOpenDeleteModal(false);
                    deleteBasket();
                }}
            />
            {id !== 'id' ? (
                <Box>
                    <StyledNormalTypography variant="h2">{panier.titre}</StyledNormalTypography>
                    <StyledMediumTypography variant="h4">{panier.poids}kg</StyledMediumTypography>
                    <Typography variant="h3">{panier.prix?.toFixed(2)}€</Typography>
                    <Typography variant="body1" sx={{ marginTop: '20px', marginBottom: '10px' }}>
                        {t('quantityOfBasketsInStock')}: {panier.quantite}
                    </Typography>
                    <StyledRowBox>
                        <Typography variant="body1">{t('cartStatus')} : </Typography>
                        <StyledMediumTypography
                            variant="body1"
                            sx={{ marginLeft: '5px', marginRight: '5px' }}>
                            {panier.estPublie ? t('published') : t('onBreak')}
                        </StyledMediumTypography>
                        <StyledButtonTable
                            type="button"
                            onClick={(): void => setIsEditMode(true)}
                            sx={{ marginTop: '-8px !important', padding: '0' }}>
                            <StyledNormalTypography
                                variant="body1"
                                sx={{
                                    textTransform: 'none',
                                    letterSpacing: 0,
                                    padding: 0
                                }}>
                                {t('edit')}
                            </StyledNormalTypography>
                        </StyledButtonTable>
                    </StyledRowBox>
                    {isEditMode && (
                        <Box sx={{ marginBottom: '10px' }}>
                            <RadioButtonSelectType
                                item={t('published')}
                                value={selectedValue}
                                onButtonSelect={(value: string): void => setSelectedValue(value)}
                            />
                            <RadioButtonSelectType
                                item={t('onBreak')}
                                value={selectedValue}
                                onButtonSelect={(value: string): void => setSelectedValue(value)}
                            />
                            <StyledRowBox>
                                <StyledWhiteButton
                                    onClick={updateStatus}
                                    sx={{
                                        border: '1px solid #394A35',
                                        minWidth: '0px !important',
                                        padding: '5px',
                                        margin: '0px'
                                    }}>
                                    <StyledMediumTypography variant="subtitle2">
                                        {t('ok')}
                                    </StyledMediumTypography>
                                </StyledWhiteButton>
                                <StyledButtonTable
                                    type="button"
                                    onClick={(): void => setIsEditMode(false)}
                                    sx={{ marginTop: '0px' }}>
                                    <StyledNormalTypography
                                        variant="body1"
                                        sx={{
                                            letterSpacing: 0,
                                            padding: 0
                                        }}>
                                        {t('capitalisedCancel')}
                                    </StyledNormalTypography>
                                </StyledButtonTable>
                            </StyledRowBox>
                        </Box>
                    )}
                    <StyledImageGrid container spacing={1}>
                        {getBasketImages()}
                    </StyledImageGrid>
                    <StyledMediumTypography variant="body1" sx={{ marginTop: '20px' }}>
                        {t('inThisBasketThereIs')}
                    </StyledMediumTypography>
                    <StyledBasketDescription variant="subtitle2" sx={{ lineBreak: 'anywhere' }}>
                        {panier.detail}
                    </StyledBasketDescription>
                    <StyledDashboardHeading variant="h3" sx={{ margin: '40px 0px 20px 0px' }}>
                        {t('myKeyIndicators')}
                    </StyledDashboardHeading>
                    <BasketIndicators panier={panier} />
                    <StyledModalButtons
                        sx={{ justifyContent: 'flex-end', margin: '20px 25px 20px 105px' }}>
                        <StyledWhiteButton onClick={(): void => setOpenDeleteModal(true)}>
                            <StyledMediumTypography variant="caption">
                                {t('toDelete')}
                            </StyledMediumTypography>
                        </StyledWhiteButton>
                        <StyledGreenButton
                            onClick={(): void =>
                                navigate('/add-basket', { state: panier.idPanier })
                            }>
                            <StyledMediumTypography
                                variant="caption"
                                color="#FFFFFF"
                                sx={{ textTransform: 'uppercase' }}>
                                {t('modifier')}
                            </StyledMediumTypography>
                        </StyledGreenButton>
                    </StyledModalButtons>
                </Box>
            ) : (
                <StyledMediumTypography variant="h5" color="#134C03">
                    {t('currentlyNoBaskets')}
                </StyledMediumTypography>
            )}
        </StyledBasketBox>
    );
}
